import { matterDetailQueryKey } from "@/lib/data/static/matter-query-key";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../static/query-keys";

import {
  createMedicalTreatmentBalanceConfirmationV3,
  deleteMedicalTreatmentBalanceConfirmationV1,
  updateMedicalTreatmentBalanceConfirmationV1,
} from "@/lib/data/requests/matters/medical-treatment-balance-confirmation";
import {
  type APIError,
  type APIResponse,
  type Matter,
  type MedicalTreatmentBalanceConfirmation,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const useCreateMedicalTreatmentBalanceConfirmation = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof createMedicalTreatmentBalanceConfirmationV3>[0],
    ) => createMedicalTreatmentBalanceConfirmationV3(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          const nextConfirmation: MedicalTreatmentBalanceConfirmation = {
            id: "optimistic",
            ...data,
          } as MedicalTreatmentBalanceConfirmation;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentBalanceConfirmations: [
                ...oldData.data.medicalTreatmentBalanceConfirmations,
                nextConfirmation,
              ],
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error creating medical treatment balance confirmation",
      );
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Balance confirmation created successfully");
      }
    },
  });
};

export const useUpdateMedicalTreatmentBalanceConfirmation = ({
  suppressSuccessToast = false,
  suppressQueryInvalidation = false,
}: {
  suppressSuccessToast?: boolean;
  suppressQueryInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof updateMedicalTreatmentBalanceConfirmationV1>[0],
    ) => updateMedicalTreatmentBalanceConfirmationV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentBalanceConfirmations:
                oldData.data.medicalTreatmentBalanceConfirmations.map(
                  (confirmation) =>
                    confirmation.id === data.id
                      ? { ...confirmation, ...data }
                      : confirmation,
                ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error updating medical treatment balance confirmation",
      );
    },
    onSettled: async () => {
      if (!suppressQueryInvalidation) {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.matters],
        });
      }
    },
    onSuccess: () => {
      if (!suppressSuccessToast) {
        toast.success("Balance confirmation updated successfully");
      }
    },
  });
};

export const useDeleteMedicalTreatmentBalanceConfirmation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Parameters<typeof deleteMedicalTreatmentBalanceConfirmationV1>[0],
    ) => deleteMedicalTreatmentBalanceConfirmationV1(data),
    onMutate: (data) => {
      queryClient.setQueryData(
        matterDetailQueryKey(data.matterId),
        (oldData: APIResponse<Matter> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              medicalTreatmentBalanceConfirmations:
                oldData.data.medicalTreatmentBalanceConfirmations.filter(
                  (confirmation) => confirmation.id !== data.id,
                ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData(
          matterDetailQueryKey(data.matterId),
        ),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          matterDetailQueryKey(variables.matterId),
          context.previousData,
        );
      }
      toastAPIErrorMessage(
        error,
        "Error deleting medical treatment balance confirmation",
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Balance confirmation deleted successfully");
    },
  });
};
